/**
 * <usdt-price> — shows the product price converted to USDT.
 *
 * Rate source: NBU USD rate. The endpoint sends `access-control-allow-origin: *`,
 * so the browser calls it directly — no proxy, no backend, no cron.
 *
 * Rules this file exists to enforce:
 * - No rate, no pill. Never a placeholder, a zero or a "loading…" state.
 *   A missing USDT price beats an invented one.
 * - Network down but cache present -> use the stale rate. It is at most a day
 *   old and the pill already says "≈".
 * - Variant without a price -> pill hides.
 */
(function () {
  'use strict';

  const RATE_URL = 'https://bank.gov.ua/NBUStatService/v1/statdirectory/exchange?valcode=USD&json';
  const STORAGE_KEY = 'theme:usdt-rate';

  /**
   * 6 hours. Not "until the NBU publishes a new rate" — there is no way to learn
   * the publication date without making the request the TTL is meant to avoid.
   * 6h caps a visitor at 4 requests a day while keeping the rate current.
   */
  const TTL = 6 * 60 * 60 * 1000;

  const EVENT_VARIANT_CHANGE = 'theme:variant:change';

  /**
   * The theme dispatches the variant event on the section (or, inside the quick
   * add modal, on the dialog) — see ProductForm.fireHookEvent. Those are the
   * elements to listen on: scoping to a single product form keeps quickview and
   * upsell pills from recalculating each other with the wrong variant.
   */
  const CONTAINERS = ['[data-section-type]', '[data-quick-add-modal]'];

  /** Single in-flight promise per page so several pills share one request. */
  let ratePromise = null;

  function readCache() {
    try {
      const cached = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!cached || typeof cached.rate !== 'number' || !(cached.rate > 0)) return null;
      return cached;
    } catch (e) {
      // Private mode and full storage both throw. Not a reason to lose the pill.
      return null;
    }
  }

  function writeCache(rate) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({rate: rate, at: Date.now()}));
    } catch (e) {
      // Nothing to do — we just go to the network next time.
    }
  }

  function fetchRate() {
    return fetch(RATE_URL)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((data) => {
        const rate = Array.isArray(data) && data[0] ? Number(data[0].rate) : NaN;
        if (!isFinite(rate) || rate <= 0) return Promise.reject('unusable rate');

        writeCache(rate);
        return rate;
      });
  }

  function getRate() {
    if (ratePromise) return ratePromise;

    const cached = readCache();
    if (cached && Date.now() - cached.at < TTL) {
      ratePromise = Promise.resolve(cached.rate);
      return ratePromise;
    }

    ratePromise = fetchRate().catch((error) => {
      if (cached) return cached.rate;
      return Promise.reject(error);
    });

    return ratePromise;
  }

  if (!customElements.get('usdt-price')) {
    customElements.define(
      'usdt-price',

      class UsdtPrice extends HTMLElement {
        constructor() {
          super();
        }

        connectedCallback() {
          this.numberEl = this.querySelector('[data-usdt-num]');
          if (!this.numberEl) return;

          this.rate = null;
          this.price = Number(this.dataset.price) || 0;

          this.container =
            CONTAINERS.reduce((found, selector) => found || this.closest(selector), null) || document;
          this.onVariantChange = this.onVariantChange.bind(this);
          this.container.addEventListener(EVENT_VARIANT_CHANGE, this.onVariantChange);

          getRate()
            .then((rate) => {
              this.rate = rate;
              this.update();
            })
            .catch(() => {
              // Stays hidden. Deliberate.
            });
        }

        disconnectedCallback() {
          if (this.container) {
            this.container.removeEventListener(EVENT_VARIANT_CHANGE, this.onVariantChange);
          }
        }

        onVariantChange(evt) {
          const variant = evt.detail ? evt.detail.variant : null;
          this.price = variant && variant.price > 0 ? variant.price : 0;
          this.update();
        }

        update() {
          if (!this.rate || !this.price) {
            this.hidden = true;
            return;
          }

          const amount = Math.round(this.price / 100 / this.rate);

          // Rounding to zero would state something untrue about the price.
          if (amount < 1) {
            this.hidden = true;
            return;
          }

          this.numberEl.textContent = amount.toLocaleString('uk-UA');
          this.hidden = false;
        }
      }
    );
  }
})();
